import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Search, ArrowRight, ChevronLeft, ChevronRight, UserPlus, X, Loader2 } from 'lucide-react';
import debounce from '@/utils/debounce';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MembersPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newMember, setNewMember] = useState({
    name: '',
    email: '',
    vorname: '',
    strasse: '',
    plz: '',
    ort: '',
    mobile: '',
    zahlungspflichtig: true,
    jahresbeitrag: 250
  });
  const [pagination, setPagination] = useState({
    total: 0,
    skip: 0,
    limit: 20
  });

  const fetchMembers = useCallback(async (searchQuery = '', skip = 0) => {
    setLoading(true);
    try {
      const params = {
        skip,
        limit: pagination.limit
      };
      
      if (searchQuery) {
        params.search = searchQuery;
      }

      const response = await axios.get(`${API_URL}/api/members`, { params });
      setMembers(response.data.members);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        skip: response.data.skip
      }));
    } catch (error) {
      console.error('Failed to fetch members:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.limit]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((query) => {
      fetchMembers(query, 0);
    }, 300),
    [fetchMembers]
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    debouncedSearch(value);
  };

  const handleNextPage = () => {
    const newSkip = pagination.skip + pagination.limit;
    if (newSkip < pagination.total) {
      fetchMembers(search, newSkip);
    }
  };

  const handlePrevPage = () => {
    const newSkip = Math.max(0, pagination.skip - pagination.limit);
    fetchMembers(search, newSkip);
  };

  const handleCreateMember = async (e) => {
    e.preventDefault();
    if (!newMember.name || !newMember.email) {
      toast.error('Name und E-Mail sind erforderlich');
      return;
    }
    
    setCreating(true);
    try {
      const token = localStorage.getItem('shrt_token');
      const response = await axios.post(
        `${API_URL}/api/admin/members`,
        newMember,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setShowCreateModal(false);
      setNewMember({
        name: '',
        email: '',
        vorname: '',
        strasse: '',
        plz: '',
        ort: '',
        mobile: '',
        zahlungspflichtig: true,
        jahresbeitrag: 250
      });
      fetchMembers(search, pagination.skip);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Erstellen des Mitglieds');
    } finally {
      setCreating(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <Layout>
      <div className="animate-slide-in">
        {/* Create Member Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card border border-border w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b border-border">
                <h2 className="font-['Oswald'] text-xl font-bold uppercase">Neues Mitglied</h2>
                <button onClick={() => setShowCreateModal(false)} className="text-muted-foreground hover:text-foreground">
                  <X size={24} />
                </button>
              </div>
              <form onSubmit={handleCreateMember} className="p-4 space-y-4">
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wider">Vorname *</label>
                  <Input
                    type="text"
                    value={newMember.vorname}
                    onChange={(e) => setNewMember(prev => ({ ...prev, vorname: e.target.value }))}
                    placeholder="Max"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wider">Nachname *</label>
                  <Input
                    type="text"
                    value={newMember.name}
                    onChange={(e) => setNewMember(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Muster"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wider">E-Mail *</label>
                  <Input
                    type="email"
                    value={newMember.email}
                    onChange={(e) => setNewMember(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="max@example.com"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wider">Strasse</label>
                  <Input
                    type="text"
                    value={newMember.strasse}
                    onChange={(e) => setNewMember(prev => ({ ...prev, strasse: e.target.value }))}
                    placeholder="Musterstrasse 1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wider">PLZ</label>
                    <Input
                      type="text"
                      value={newMember.plz}
                      onChange={(e) => setNewMember(prev => ({ ...prev, plz: e.target.value }))}
                      placeholder="8000"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wider">Ort</label>
                    <Input
                      type="text"
                      value={newMember.ort}
                      onChange={(e) => setNewMember(prev => ({ ...prev, ort: e.target.value }))}
                      placeholder="Zürich"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wider">Mobile</label>
                  <Input
                    type="text"
                    value={newMember.mobile}
                    onChange={(e) => setNewMember(prev => ({ ...prev, mobile: e.target.value }))}
                    placeholder="079 123 45 67"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wider">Jahresbeitrag</label>
                    <Input
                      type="number"
                      value={newMember.jahresbeitrag}
                      onChange={(e) => setNewMember(prev => ({ ...prev, jahresbeitrag: parseFloat(e.target.value) }))}
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-6">
                    <input
                      type="checkbox"
                      id="zahlungspflichtig"
                      checked={newMember.zahlungspflichtig}
                      onChange={(e) => setNewMember(prev => ({ ...prev, zahlungspflichtig: e.target.checked }))}
                      className="w-4 h-4"
                    />
                    <label htmlFor="zahlungspflichtig" className="text-sm">Zahlungspflichtig</label>
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 rounded-none"
                  >
                    Abbrechen
                  </Button>
                  <Button
                    type="submit"
                    disabled={creating}
                    className="flex-1 rounded-none"
                  >
                    {creating ? <Loader2 className="animate-spin mr-2" size={16} /> : <UserPlus className="mr-2" size={16} />}
                    Erstellen
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="font-['Oswald'] text-2xl sm:text-3xl md:text-4xl font-bold text-foreground uppercase tracking-tight mb-1 sm:mb-2">
              Mitglieder
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground">
              {pagination.total} Mitglieder insgesamt
            </p>
          </div>
          {isAdmin && (
            <Button
              onClick={() => setShowCreateModal(true)}
              className="rounded-none uppercase tracking-widest font-bold text-sm w-full sm:w-auto"
              data-testid="create-member-btn"
            >
              <UserPlus className="mr-2" size={16} />
              Neues Mitglied
            </Button>
          )}
        </div>

        {/* Search */}
        <div className="relative mb-4 sm:mb-6">
          <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
          <Input
            type="text"
            placeholder="Suchen..."
            value={search}
            onChange={handleSearchChange}
            className="search-input pl-10 sm:pl-12"
            data-testid="search-input"
          />
        </div>

        {/* Members List - Cards on mobile, Table on desktop */}
        <div className="bg-card border border-border overflow-hidden">
          {/* Mobile Card View */}
          <div className="sm:hidden divide-y divide-border">
            {loading ? (
              [...Array(5)].map((_, i) => (
                <div key={i} className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 skeleton rounded-full" />
                    <div className="flex-1">
                      <div className="h-4 w-32 skeleton mb-2" />
                      <div className="h-3 w-48 skeleton" />
                    </div>
                  </div>
                </div>
              ))
            ) : members.length > 0 ? (
              members.map((member) => (
                <div
                  key={member.id}
                  onClick={() => navigate(`/members/${member.id}`)}
                  className="p-4 cursor-pointer hover:bg-muted/50 transition-colors active:bg-muted"
                  data-testid={`member-card-${member.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="avatar-placeholder flex-shrink-0">
                      {getInitials(member.name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-foreground truncate">{member.name}</span>
                        <span className={`status-badge text-xs flex-shrink-0 ${member.is_active ? 'active' : 'inactive'}`}>
                          {member.is_active ? 'Aktiv' : 'Inaktiv'}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground truncate mt-0.5">{member.email}</p>
                    </div>
                    <ArrowRight size={18} className="text-muted-foreground flex-shrink-0" />
                  </div>
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-muted-foreground">
                {search ? 'Keine Mitglieder gefunden' : 'Keine Mitglieder vorhanden'}
              </div>
            )}
          </div>

          {/* Desktop Table View */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="members-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>E-Mail</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 skeleton" />
                          <div className="h-4 w-32 skeleton" />
                        </div>
                      </td>
                      <td><div className="h-4 w-48 skeleton" /></td>
                      <td><div className="h-6 w-16 skeleton" /></td>
                      <td><div className="h-4 w-4 skeleton" /></td>
                    </tr>
                  ))
                ) : members.length > 0 ? (
                  members.map((member) => (
                    <tr 
                      key={member.id}
                      onClick={() => navigate(`/members/${member.id}`)}
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      data-testid={`member-row-${member.id}`}
                    >
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="avatar-placeholder">
                            {getInitials(member.name)}
                          </div>
                          <span className="font-medium text-foreground">{member.name}</span>
                        </div>
                      </td>
                      <td className="text-muted-foreground">{member.email}</td>
                      <td>
                        <span className={`status-badge ${member.is_active ? 'active' : 'inactive'}`}>
                          {member.is_active ? 'Aktiv' : 'Inaktiv'}
                        </span>
                      </td>
                      <td>
                        <ArrowRight size={18} className="text-muted-foreground" />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center py-12 text-muted-foreground">
                      {search ? 'Keine Mitglieder gefunden' : 'Keine Mitglieder vorhanden'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.total > pagination.limit && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-border">
              <p className="text-xs sm:text-sm text-muted-foreground order-2 sm:order-1">
                <span className="hidden sm:inline">Zeige </span>{pagination.skip + 1} - {Math.min(pagination.skip + pagination.limit, pagination.total)} von {pagination.total}
              </p>
              <div className="flex items-center gap-2 order-1 sm:order-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handlePrevPage}
                  disabled={pagination.skip === 0}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="prev-page"
                >
                  <ChevronLeft size={18} />
                </Button>
                <span className="text-xs sm:text-sm text-muted-foreground min-w-[80px] text-center">
                  {currentPage} / {totalPages}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleNextPage}
                  disabled={pagination.skip + pagination.limit >= pagination.total}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="next-page"
                >
                  <ChevronRight size={18} />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
